/**
 * User Service   Firestore
 *
 * Stores authenticated users, tracks how many cargo-tracking
 * requests each user makes, and saves each individual tracking
 * record in a subcollection.
 *
 * Firestore structure:
 *   users/{uid}
 *     displayName, email, photoURL, provider,
 *     createdAt, lastLoginAt,
 *     trackingRequests: { sea: N, air: N, total: N }
 *
 *   users/{uid}/trackings/{autoId}
 *     type ('sea'|'air'), trackingNumber, destination,
 *     status, carrier, trackedAt
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from './firebase';

const USERS_COL = 'users';

/* -------------------------------------------------- */
/*  Save / update a user document on every sign-in    */
/* -------------------------------------------------- */

export async function saveUser(firebaseUser) {
  if (!firebaseUser) return;

  const uid = firebaseUser.uid;
  const ref = doc(db, USERS_COL, uid);

  try {
    // Always merge   creates if missing, updates if exists.
    // This avoids a read-before-write and handles race conditions.
    await setDoc(ref, {
      uid,
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || '',
      photoURL: firebaseUser.photoURL || '',
      provider: firebaseUser.providerData?.[0]?.providerId || 'google.com',
      lastLoginAt: serverTimestamp(),
    }, { merge: true });

    // Ensure trackingRequests map exists for brand-new docs
    const snap = await getDoc(ref);
    if (!snap.data()?.trackingRequests) {
      await setDoc(ref, {
        createdAt: serverTimestamp(),
        trackingRequests: { sea: 0, air: 0, total: 0 },
      }, { merge: true });
    }
  } catch (err) {
    console.error('saveUser error:', err);
  }
}

/* -------------------------------------------------- */
/*  Increment tracking request counter                */
/* -------------------------------------------------- */

/**
 * @param {string} uid  Firebase user uid
 * @param {'sea'|'air'} type  Which tracking mode
 * @param {object} details  Extra info about the tracking
 * @param {string} details.trackingNumber
 * @param {string} [details.destination]
 * @param {string} [details.status]
 * @param {string} [details.carrier]
 */
export async function recordTrackingRequest(uid, type, details = {}) {
  if (!uid || !type) return;

  const userRef = doc(db, USERS_COL, uid);
  try {
    // 1) Ensure user doc exists, then increment counters.
    //    setDoc with merge creates the doc if it's missing.
    await setDoc(userRef, {
      uid,
      lastActiveAt: serverTimestamp(),
    }, { merge: true });

    await updateDoc(userRef, {
      [`trackingRequests.${type}`]: increment(1),
      [`trackingRequests.total`]: increment(1),
    });

    // 2) Save individual tracking record in subcollection
    const trackingsCol = collection(db, USERS_COL, uid, 'trackings');
    await addDoc(trackingsCol, {
      type,
      trackingNumber: details.trackingNumber || '',
      destination: details.destination || '',
      status: details.status || '',
      carrier: details.carrier || '',
      trackedAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('recordTrackingRequest error:', err);
  }
}

/* -------------------------------------------------- */
/*  Fetch a user's recent tracking history            */
/* -------------------------------------------------- */

/**
 * @param {string} uid
 * @param {number} maxResults  How many recent records to fetch (default 20)
 */
export async function getUserTrackings(uid, maxResults = 20) {
  if (!uid) return [];
  try {
    const q = query(
      collection(db, USERS_COL, uid, 'trackings'),
      orderBy('trackedAt', 'desc'),
      limit(maxResults),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('getUserTrackings error:', err);
    return [];
  }
}

/* -------------------------------------------------- */
/*  Fetch the current user's stats                    */
/* -------------------------------------------------- */

export async function getUserStats(uid) {
  if (!uid) return null;
  try {
    const snap = await getDoc(doc(db, USERS_COL, uid));
    if (snap.exists()) return snap.data();
    return null;
  } catch (err) {
    console.error('getUserStats error:', err);
    return null;
  }
}

/* -------------------------------------------------- */
/*  Admin: fetch ALL users ordered by total requests  */
/* -------------------------------------------------- */

export async function getAllUsers() {
  try {
    const q = query(
      collection(db, USERS_COL),
      orderBy('trackingRequests.total', 'desc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('getAllUsers error:', err);
    return [];
  }
}
